import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { startOfDay, startOfWeek, startOfMonth, endOfDay, endOfWeek, endOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pb, getRestaurantId } from '../config/pocketbase';
import { supabase } from '../config/supabase';
import { exportToExcel } from '../utils/excelExport';

const { FiBarChart3, FiCalendar, FiTrendingUp, FiDownload, FiTrash2, FiAlertTriangle, FiDollarSign } = FiIcons;

const Statistiche = () => {
  const [timeFilter, setTimeFilter] = useState('giorno');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchStatistics();
  }, [timeFilter]);

  const getDateRange = () => {
    const now = new Date();
    switch (timeFilter) {
      case 'giorno':
        return { start: startOfDay(now), end: endOfDay(now) };
      case 'settimana':
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'mese':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      default:
        return { start: startOfDay(now), end: endOfDay(now) };
    }
  };

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const restaurantId = getRestaurantId();
      let comande = [];

      try {
        // Query PocketBase
        let filterStr = `timestamp >= "${start.toISOString()}" && timestamp <= "${end.toISOString()}"`;
        if (restaurantId && restaurantId !== 'all') {
          filterStr += ` && (ristorante_id = "${restaurantId}" || ristorante_id = "" || ristorante_id = "default")`;
        }
        comande = await pb.collection('comande_tap2order').getFullList({
          filter: filterStr,
          requestKey: null
        });
      } catch (pbErr) {
        console.warn('[Statistiche] PocketBase non disponibile, fallback Supabase:', pbErr?.message);
        const { data, error } = await supabase
          .from('comande_tap2order')
          .select('piatti, timestamp, codice_comanda, status')
          .gte('timestamp', start.toISOString())
          .lte('timestamp', end.toISOString());
        if (error) throw error;
        comande = data || [];
      }

      // Count dish frequencies and calculate revenue
      const dishCount = {};
      let totalOrdersCount = 0;
      let totalRevenueAmount = 0;

      comande.forEach(comanda => {
        totalOrdersCount++;
        const piattiList = Array.isArray(comanda.piatti) ? comanda.piatti : [];
        piattiList.forEach(piatto => {
          const key = piatto.nome;
          dishCount[key] = dishCount[key] || { quantita: 0, prezzo: piatto.prezzo || 0 };
          dishCount[key].quantita += piatto.quantita || 1;
          totalRevenueAmount += (piatto.prezzo || 0) * (piatto.quantita || 1);
        });
      });

      // Convert to chart format and sort by frequency
      const chartDataArray = Object.entries(dishCount)
        .map(([nome, data]) => ({
          nome,
          quantita: data.quantita,
          prezzo: data.prezzo
        }))
        .sort((a, b) => b.quantita - a.quantita)
        .slice(0, 10); // Top 10

      setChartData(chartDataArray);
      setTotalOrders(totalOrdersCount);
      setTotalRevenue(totalRevenueAmount);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    if (chartData.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setIsExporting(true);
    try {
      const success = exportToExcel(chartData, totalRevenue, totalOrders, `statistiche_${timeFilter}`);
      if (success) {
        alert('Statistiche esportate con successo!');
      } else {
        alert('Errore durante l\'esportazione');
      }
    } catch (error) {
      console.error('Errore esportazione:', error);
      alert('Errore durante l\'esportazione');
    } finally {
      setIsExporting(false);
    }
  };

  const handleResetStatistics = async () => {
    setIsResetting(true);
    try {
      const restaurantId = getRestaurantId();
      try {
        const records = await pb.collection('comande_tap2order').getFullList({
          filter: restaurantId && restaurantId !== 'all' ? `ristorante_id = "${restaurantId}"` : '',
          requestKey: null
        });
        await Promise.all(records.map(r => pb.collection('comande_tap2order').delete(r.id)));
      } catch {
        await supabase
          .from('comande_tap2order')
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000');
      }

      await fetchStatistics();
      setShowResetModal(false);
      alert('Statistiche azzerate con successo!');
    } catch (error) {
      console.error('Errore azzeramento statistiche:', error);
      alert('Errore durante l\'azzeramento delle statistiche');
    } finally {
      setIsResetting(false);
    }
  };

  const getChartOption = () => {
    return {
      title: {
        text: 'Piatti Più Ordinati',
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold',
          color: '#374151'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const data = params[0];
          const item = chartData[data.dataIndex];
          return `${data.name}<br/>Quantità: ${data.value}<br/>Fatturato: €${(item.prezzo * item.quantita).toFixed(2)}`;
        }
      },
      xAxis: {
        type: 'category',
        data: chartData.map(item => item.nome),
        axisLabel: {
          rotate: 45,
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        name: 'Quantità',
        nameTextStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          type: 'bar',
          data: chartData.map(item => item.quantita),
          itemStyle: {
            color: '#5C715E'
          },
          emphasis: {
            itemStyle: {
              color: '#4B5C4D'
            }
          }
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      }
    };
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C715E]"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-7xl mx-auto px-4 py-8"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiBarChart3} className="w-10 h-10 mr-3" />
            Analisi Ordini
          </h1>
          <p className="text-lg text-gray-600">
            Statistiche dettagliate delle vendite e report esportabili
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExportToExcel}
            disabled={chartData.length === 0 || isExporting}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            <SafeIcon icon={FiDownload} className="w-5 h-5 mr-2" />
            {isExporting ? 'Esportando...' : 'Esporta Excel'}
          </button>
          <button
            onClick={() => setShowResetModal(true)}
            className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center"
          >
            <SafeIcon icon={FiTrash2} className="w-5 h-5 mr-2" />
            Azzera Statistiche
          </button>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <AnimatePresence>
        {showResetModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Conferma Azzeramento</h3>
                  <p className="text-gray-600 text-sm">Questa azione non può essere annullata</p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 text-sm">
                  <strong>Attenzione:</strong> Stai per eliminare TUTTE le comande storiche dal database. 
                  Questa operazione cancellerà definitivamente tutti i dati delle statistiche.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResetModal(false)}
                  disabled={isResetting}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleResetStatistics}
                  disabled={isResetting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {isResetting ? 'Azzerando...' : 'Conferma Azzeramento'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Time Filter */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="flex items-center space-x-2 mb-4">
          <SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-700">Periodo di Analisi:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {['giorno', 'settimana', 'mese'].map((period) => (
            <button
              key={period}
              onClick={() => setTimeFilter(period)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                timeFilter === period
                  ? 'bg-[#5C715E] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Comande Totali</p>
              <p className="text-3xl font-bold text-gray-900">{totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          delay={0.1}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Fatturato Totale</p>
              <p className="text-3xl font-bold text-gray-900">
                €{totalRevenue.toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          delay={0.2}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Media per Comanda</p>
              <p className="text-3xl font-bold text-gray-900">
                €{totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          delay={0.3}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Piatti Diversi</p>
              <p className="text-3xl font-bold text-gray-900">{chartData.length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        delay={0.4}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        {chartData.length > 0 ? (
          <ReactECharts
            option={getChartOption()}
            style={{ height: '500px' }}
            opts={{ renderer: 'svg' }}
          />
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiBarChart3} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              Nessun dato disponibile per il periodo selezionato
            </p>
          </div>
        )}
      </motion.div>

      {/* Detailed Statistics Table */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          delay={0.5}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              Tabella Dettagliata - Classifica Piatti
            </h2>
            <span className="text-sm text-gray-500">
              {chartData.length} piatti analizzati
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Posizione</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Nome Piatto</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">Quantità</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">Percentuale</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">Fatturato</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => {
                  const totalQuantity = chartData.reduce((sum, d) => sum + d.quantita, 0);
                  const percentage = ((item.quantita / totalQuantity) * 100).toFixed(2);
                  const itemRevenue = (item.prezzo * item.quantita).toFixed(2);
                  
                  return (
                    <tr key={item.nome} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-500' : 'bg-[#5C715E]'
                        }`}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900">{item.nome}</td>
                      <td className="py-3 px-4 text-center font-bold text-[#5C715E]">{item.quantita}</td>
                      <td className="py-3 px-4 text-center text-gray-600">{percentage}%</td>
                      <td className="py-3 px-4 text-center font-semibold text-green-600">€{itemRevenue}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Statistiche;